import React, { useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';

interface ExcelImportPopupProps {
  visible: boolean;
  onHide: () => void;
  templateUrl: string;
  onUpload: (file: File) => Promise<void>;
  title?: string;
}

const ALLOWED_EXCEL_EXTENSIONS = ['xlsx', 'xls'];
const ALLOWED_EXCEL_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
];

const isExcelFile = (file: File): boolean => {
  const fileNameParts = file.name.toLowerCase().split('.');
  const extension = fileNameParts.length > 1 ? fileNameParts[fileNameParts.length - 1] : '';
  const isAllowedExtension = ALLOWED_EXCEL_EXTENSIONS.includes(extension);
  const isAllowedMimeType = ALLOWED_EXCEL_MIME_TYPES.includes(file.type);

  // Some browsers may provide empty mime type, so extension check is the primary gate.
  return isAllowedExtension || (file.type !== '' && isAllowedMimeType);
};

const ExcelImportPopup: React.FC<ExcelImportPopupProps> = ({
  visible,
  onHide,
  templateUrl,
  onUpload,
  title = 'Import Excel File',
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const toast = useRef<Toast>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selected = e.target.files[0];
      if (!isExcelFile(selected)) {
        setSelectedFile(null);
        e.target.value = '';
        toast.current?.show({
          severity: 'error',
          summary: 'Invalid File Type',
          detail: 'Only Excel files (.xlsx, .xls) are allowed.',
        });
        return;
      }
      setSelectedFile(selected);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.current?.show({ severity: 'warn', summary: 'No File', detail: 'Please select a file to upload.' });
      return;
    }
    if (!isExcelFile(selectedFile)) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Only Excel files (.xlsx, .xls) are allowed.',
      });
      setSelectedFile(null);
      return;
    }
    setUploading(true);
    try {
      await onUpload(selectedFile);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'File uploaded successfully.' });
      setSelectedFile(null);
      onHide();
    } catch (err: unknown) {
      const detail = err instanceof Error ? err.message : 'Upload failed.';
      toast.current?.show({ severity: 'error', summary: 'Error', detail });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog header={title} visible={visible} style={{ width: '30vw' }} onHide={onHide} modal>
      <Toast ref={toast} position="top-right" style={{ zIndex: 10000 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <a href={templateUrl} target="_blank" rel="noopener noreferrer" style={{ marginBottom: 8 }}>
          Download Excel Template
        </a>
        <InputText type="file" accept=".xlsx,.xls" onChange={handleFileChange} />
        <Button label="Upload" icon="pi pi-upload" onClick={handleUpload} loading={uploading} disabled={!selectedFile || uploading} />
      </div>
    </Dialog>
  );
};

export default ExcelImportPopup;
